import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_BASE_URL } from '@core/config/api.config';
import { ApiEndpoints } from '@core/config/api-endpoints';
import { PosInformeDashboard } from '../models/pos-informe.models';
import {
  PosCashOutListQuery,
  PosCashOutListResult,
  PosCashOutResume,
  PosCompleteCashOutResult,
  PosVoidCashOutResult,
} from '../models/pos-traslado.models';
import { PosTransaccionesDashboard } from '../models/pos-transacciones.models';
import {
  ensureNoTopLevelErrors,
  GraphqlHttpResponse,
} from './mappers/pos-catalog.mapper';
import { mapPosInformeDashboard } from './mappers/pos-informe.mapper';
import { mapPosCashOutResume, mapPosCashOutRow } from './mappers/pos-traslado.mapper';
import { mapPosTransaccionesDashboard } from './mappers/pos-transacciones.mapper';

const VENTAS = `
  query PosVentasDashboard(
    $locationId: ID!
    $reportDate: ISO8601Date
    $startDatetime: ISO8601DateTime
    $endDatetime: ISO8601DateTime
  ) {
    posVentasDashboard(
      locationId: $locationId
      reportDate: $reportDate
      startDatetime: $startDatetime
      endDatetime: $endDatetime
    ) {
      locationId
      reportDate
      startDatetime
      endDatetime
      totalVentas
      totalPersonalizado
      totalGeneral
      totalPropinas
      totalDescuentoCarrito
      totalDescuentoArticulo
      totalEnvios
      totalReembolso
      ordenesCompletadas
      ordenesAnuladas
      hasData
      metodosPago
      ventasPorCategoria
      ventasPorProducto
      ordenesAnuladasDetalle
    }
  }
`;

const TX = `
  query PosTransaccionesDashboard(
    $locationId: ID!
    $page: Int
    $startDatetime: ISO8601DateTime
    $endDatetime: ISO8601DateTime
    $paymentMethods: [String!]
    $status: String
    $q: String
    $per: Int
  ) {
    posTransaccionesDashboard(
      locationId: $locationId
      page: $page
      startDatetime: $startDatetime
      endDatetime: $endDatetime
      paymentMethods: $paymentMethods
      status: $status
      q: $q
      per: $per
    ) {
      locationId
      startDatetime
      endDatetime
      payload
    }
  }
`;

const CANCEL = `
  mutation CancelPosOrder($id: ID!, $cancellationNote: String!) {
    cancelPosOrder(id: $id, cancellationNote: $cancellationNote) {
      order { id orderNumber status }
      errors
    }
  }
`;

const CASH_OUTS = `
  query PosCashOuts(
    $locationId: ID!
    $id: ID
    $q: String
    $userId: ID
    $dateField: String
    $startDate: ISO8601Date
    $endDate: ISO8601Date
    $page: Int
    $per: Int
  ) {
    posCashOuts(
      locationId: $locationId
      id: $id
      q: $q
      userId: $userId
      dateField: $dateField
      startDate: $startDate
      endDate: $endDate
      page: $page
      per: $per
    ) {
      items {
        id
        number
        cashOutDate
        locationId
        userId
        userName
        totalVenta
        totalVentaBruta
        tipsPayableTotal
        amountLines {
          accountId
          name
          net
          feeProfile
        }
        createdAt
        status
        voided
        voidReason
        voidedAt
      }
      totalCount
      page
      per
    }
  }
`;

const CASH_OUT_RESUME = `
  query PosCashOutResume(
    $locationId: ID!
    $startDatetime: ISO8601DateTime!
    $endDatetime: ISO8601DateTime!
    $cashOutDate: ISO8601Date
  ) {
    posCashOutResume(
      locationId: $locationId
      startDatetime: $startDatetime
      endDatetime: $endDatetime
      cashOutDate: $cashOutDate
    ) {
      startDatetime
      endDatetime
      cashOutDate
      totalGeneral
      tipTotal
      amountLines {
        accountId
        name
        net
        gross
        feeProfile
      }
      categoryTotals
      ordersCount
      orders
      occupiedTables
      openOrdersCount
      pendingManagerCartsCount
      operationBlockingError
      openTipShifts
      alreadyExists
      periodOverlap
      unmatchedAccountNames
      missingTipsPayableAccount
      canComplete
    }
  }
`;

const COMPLETE_CASH_OUT = `
  mutation CompletePosCashOut(
    $locationId: ID!
    $startDatetime: ISO8601DateTime!
    $endDatetime: ISO8601DateTime!
    $cashOutDate: ISO8601Date
    $confirmedEmployeeIds: [ID!]
  ) {
    completePosCashOut(
      locationId: $locationId
      startDatetime: $startDatetime
      endDatetime: $endDatetime
      cashOutDate: $cashOutDate
      confirmedEmployeeIds: $confirmedEmployeeIds
    ) {
      cashOut {
        id
        number
        cashOutDate
        locationId
        userId
        userName
        totalVenta
        totalVentaBruta
        tipsPayableTotal
        amountLines {
          accountId
          name
          net
          feeProfile
        }
        createdAt
        status
        voided
        voidReason
        voidedAt
      }
      errors
      warnings
    }
  }
`;

const VOID_CASH_OUT = `
  mutation VoidPosCashOut($id: ID!, $reason: String!, $notes: String!) {
    voidPosCashOut(id: $id, reason: $reason, notes: $notes) {
      cashOut {
        id
        number
        cashOutDate
        locationId
        userId
        userName
        totalVenta
        totalVentaBruta
        tipsPayableTotal
        amountLines {
          accountId
          name
          net
          feeProfile
        }
        createdAt
        status
        voided
        voidReason
        voidedAt
      }
      errors
    }
  }
`;

@Injectable()
export class PosReportsApiService {
  constructor(
    private readonly http: HttpClient,
    @Inject(API_BASE_URL) private readonly apiBaseUrl: string
  ) {}

  ventasDashboard(
    locationId: string,
    options: {
      reportDate?: string;
      startDatetime?: string;
      endDatetime?: string;
    } = {}
  ): Observable<PosInformeDashboard> {
    return this.http
      .post<GraphqlHttpResponse<{ posVentasDashboard: Record<string, unknown> }>>(
        `${this.apiBaseUrl}${ApiEndpoints.graphql}`,
        {
          query: VENTAS,
          variables: {
            locationId,
            reportDate: options.reportDate ?? null,
            startDatetime: options.startDatetime ?? null,
            endDatetime: options.endDatetime ?? null,
          },
        }
      )
      .pipe(
        map((r) => ensureNoTopLevelErrors(r)),
        map((d) => {
          if (!d.posVentasDashboard) {
            throw new Error('Respuesta de informe vacía');
          }
          return mapPosInformeDashboard(d.posVentasDashboard);
        })
      );
  }

  transaccionesDashboard(
    locationId: string,
    options: {
      page?: number;
      per?: number;
      startDatetime?: string;
      endDatetime?: string;
      paymentMethods?: readonly string[];
      status?: string;
      q?: string;
    } = {}
  ): Observable<PosTransaccionesDashboard> {
    const methods = (options.paymentMethods ?? []).map((m) => m.trim()).filter(Boolean);
    return this.http
      .post<GraphqlHttpResponse<{ posTransaccionesDashboard: Record<string, unknown> }>>(
        `${this.apiBaseUrl}${ApiEndpoints.graphql}`,
        {
          query: TX,
          variables: {
            locationId,
            page: options.page ?? 1,
            per: options.per ?? 50,
            startDatetime: options.startDatetime ?? null,
            endDatetime: options.endDatetime ?? null,
            paymentMethods: methods.length ? methods : null,
            status: options.status && options.status !== 'all' ? options.status : null,
            q: options.q?.trim() || null,
          },
        }
      )
      .pipe(
        map((r) => ensureNoTopLevelErrors(r)),
        map((d) => {
          if (!d.posTransaccionesDashboard) {
            throw new Error('Respuesta de transacciones vacía');
          }
          return mapPosTransaccionesDashboard(d.posTransaccionesDashboard);
        })
      );
  }

  cancelOrder(id: string, cancellationNote: string): Observable<{ errors: string[] }> {
    return this.http
      .post<GraphqlHttpResponse<{ cancelPosOrder: { errors: string[] } }>>(
        `${this.apiBaseUrl}${ApiEndpoints.graphql}`,
        { query: CANCEL, variables: { id, cancellationNote } }
      )
      .pipe(
        map((r) => ensureNoTopLevelErrors(r)),
        map((d) => ({ errors: d.cancelPosOrder.errors ?? [] }))
      );
  }

  listCashOuts(
    locationId: string,
    options: PosCashOutListQuery = {}
  ): Observable<PosCashOutListResult> {
    return this.http
      .post<GraphqlHttpResponse<{ posCashOuts: Record<string, unknown> }>>(
        `${this.apiBaseUrl}${ApiEndpoints.graphql}`,
        {
          query: CASH_OUTS,
          variables: {
            locationId,
            id: options.id?.trim() || null,
            q: options.q?.trim() || null,
            userId: options.userId?.trim() || null,
            dateField: options.dateField ?? 'cash_out_date',
            startDate: options.startDate ?? null,
            endDate: options.endDate ?? null,
            page: options.page ?? 1,
            per: options.per ?? 10,
          },
        }
      )
      .pipe(
        map((r) => ensureNoTopLevelErrors(r)),
        map((d) => {
          const payload = d.posCashOuts ?? {};
          const rawItems = Array.isArray(payload['items']) ? payload['items'] : [];
          return {
            items: rawItems
              .map((row) => mapPosCashOutRow((row ?? {}) as Record<string, unknown>))
              .filter((row) => row.id),
            totalCount: Number(payload['totalCount'] ?? payload['total_count'] ?? 0) || 0,
            page: Number(payload['page'] ?? options.page ?? 1) || 1,
            per: Number(payload['per'] ?? options.per ?? 10) || 10,
          };
        })
      );
  }

  cashOutResume(
    locationId: string,
    startDatetime: string,
    endDatetime: string,
    cashOutDate?: string
  ): Observable<PosCashOutResume> {
    return this.http
      .post<GraphqlHttpResponse<{ posCashOutResume: Record<string, unknown> }>>(
        `${this.apiBaseUrl}${ApiEndpoints.graphql}`,
        {
          query: CASH_OUT_RESUME,
          variables: {
            locationId,
            startDatetime,
            endDatetime,
            cashOutDate: cashOutDate ?? null,
          },
        }
      )
      .pipe(
        map((r) => ensureNoTopLevelErrors(r)),
        map((d) => {
          if (!d.posCashOutResume) {
            throw new Error('Respuesta de resumen de traslado vacía');
          }
          return mapPosCashOutResume(d.posCashOutResume);
        })
      );
  }

  completeCashOut(
    locationId: string,
    options: {
      startDatetime: string;
      endDatetime: string;
      cashOutDate?: string;
      confirmedEmployeeIds?: readonly string[];
    }
  ): Observable<PosCompleteCashOutResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          completePosCashOut: {
            cashOut: Record<string, unknown> | null;
            errors: string[];
            warnings: string[];
          };
        }>
      >(`${this.apiBaseUrl}${ApiEndpoints.graphql}`, {
        query: COMPLETE_CASH_OUT,
        variables: {
          locationId,
          startDatetime: options.startDatetime,
          endDatetime: options.endDatetime,
          cashOutDate: options.cashOutDate ?? null,
          confirmedEmployeeIds: options.confirmedEmployeeIds?.length
            ? [...options.confirmedEmployeeIds]
            : null,
        },
      })
      .pipe(
        map((r) => ensureNoTopLevelErrors(r)),
        map((d) => {
          const payload = d.completePosCashOut;
          return {
            cashOut: payload?.cashOut ? mapPosCashOutRow(payload.cashOut) : null,
            errors: payload?.errors ?? [],
            warnings: payload?.warnings ?? [],
          };
        })
      );
  }

  voidCashOut(
    id: string,
    reason: string,
    notes: string
  ): Observable<PosVoidCashOutResult> {
    return this.http
      .post<
        GraphqlHttpResponse<{
          voidPosCashOut: {
            cashOut: Record<string, unknown> | null;
            errors: string[];
          };
        }>
      >(`${this.apiBaseUrl}${ApiEndpoints.graphql}`, {
        query: VOID_CASH_OUT,
        variables: { id, reason, notes },
      })
      .pipe(
        map((r) => ensureNoTopLevelErrors(r)),
        map((d) => {
          const payload = d.voidPosCashOut;
          return {
            cashOut: payload?.cashOut ? mapPosCashOutRow(payload.cashOut) : null,
            errors: payload?.errors ?? [],
          };
        })
      );
  }
}
